import { useEffect } from 'react';
import { LocalNotifications } from '@capacitor/local-notifications';
import { isNativePlatform } from '@/lib/platform';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { getNextNMarketDays, isMarketOpen } from '@/lib/marketCalendar';
import { useNotificationPreferences } from './useNotificationPreferences';

export const useDailyPulseNotifications = (userId: string | undefined) => {
  const { toast } = useToast();
  const { preferences, isLoading: prefsLoading } = useNotificationPreferences(userId);

  useEffect(() => {
    const setupDailyNotifications = async () => {
      if (!isNativePlatform() || !userId || prefsLoading) return;

      // Check if notifications are enabled in preferences
      if (!preferences.enabled) {
        logger.info('useDailyPulseNotifications', 'Notifications disabled in preferences');
        return;
      }

      try {
        // Request permissions
        const permission = await LocalNotifications.requestPermissions();
        
        if (permission.display !== 'granted') {
          logger.info('useDailyPulseNotifications', 'Notification permission not granted');
          return;
        }

        // Clear any existing daily pulse notifications
        const pending = await LocalNotifications.getPending();
        const dailyPulseIds = pending.notifications
          .filter(n => n.extra?.type === 'daily_pulse')
          .map(n => n.id);
        
        if (dailyPulseIds.length > 0) {
          await LocalNotifications.cancel({ notifications: dailyPulseIds.map(id => ({ id })) });
        }

        // Schedule notifications for next 30 market days only
        const nextMarketDays = getNextNMarketDays(30);
        const notifications = nextMarketDays.map((date, index) => {
          const notificationTime = new Date(date);
          notificationTime.setHours(preferences.hour, preferences.minute, 0, 0);
          
          return {
            id: 1001 + index,
            title: '📊 Set Your Daily Market Bias',
            body: 'What\'s your outlook for today\'s trading?',
            schedule: { at: notificationTime },
            extra: { type: 'daily_pulse', action: 'morning_reminder' }
          };
        });
        
        await LocalNotifications.schedule({ notifications });
        
        logger.info('useDailyPulseNotifications', `Scheduled ${notifications.length} market day notifications`);
      } catch (error) {
        logger.error('useDailyPulseNotifications:setup', error);
      }
    };

    // Add listener to check if bias is already set before showing notification
    const setupListener = async () => {
      if (!isNativePlatform() || !userId) return;

      try {
        await LocalNotifications.addListener(
          'localNotificationReceived',
          async (notification) => {
            if (notification.extra?.type === 'daily_pulse') {
              const now = new Date();
              
              // Suppress if market is closed (weekend or holiday)
              if (!isMarketOpen(now)) {
                await LocalNotifications.cancel({ 
                  notifications: [{ id: notification.id }] 
                });
                logger.info('useDailyPulseNotifications', 'Notification suppressed - market closed');
                return;
              }

              // Check if user already set bias today
              const today = format(new Date(), 'yyyy-MM-dd');
              const { data } = await supabase
                .from('daily_sentiment_votes')
                .select('vote')
                .eq('user_id', userId)
                .eq('date', today)
                .eq('category', 'market_bias')
                .maybeSingle();
              
              // If bias exists, cancel the notification
              if (data?.vote) {
                await LocalNotifications.cancel({ 
                  notifications: [{ id: notification.id }] 
                });
                logger.info('useDailyPulseNotifications', 'Notification suppressed - bias already set');
              }
            }
          }
        );
      } catch (error) {
        logger.error('useDailyPulseNotifications:listener', error);
      }
    };

    setupDailyNotifications();
    setupListener();

    // Cleanup
    return () => {
      if (isNativePlatform()) {
        LocalNotifications.removeAllListeners();
      }
    };
  }, [userId, preferences, prefsLoading]);

  const sendImmediateNotification = async (title: string, body: string) => {
    try {
      if (isNativePlatform()) {
        await LocalNotifications.schedule({
          notifications: [{
            id: Math.floor(Math.random() * 10000),
            title,
            body,
            schedule: { at: new Date(Date.now() + 1000) }
          }]
        });
      } else {
        toast({ title, description: body });
      }
    } catch (error) {
      logger.error('useDailyPulseNotifications:immediate', error);
    }
  };

  return { sendImmediateNotification };
};
